import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { createUploadLink } from 'apollo-upload-client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import Cookies from 'universal-cookie';
import { GRAPHQL_ENDPOINT, JWT_COOKIE_KEY } from './consts';
import { formatError, formatMessage } from './utils';

const uniqBy = require('lodash.uniqby');
const endpointURL = new URL(GRAPHQL_ENDPOINT);

/**
 * For more information on how to use the uploadLink,
 * See:
 *    https://www.apollographql.com/blog/graphql/file-uploads/with-react-hooks-typescript-amazon-s3-tutorial/
 */
const uploadLink = createUploadLink({ uri: GRAPHQL_ENDPOINT });

const portalName = (): string => {
  const pathname = window.location.pathname;
  if (pathname.length < 2) return '';
  const end = pathname.indexOf('/', 1);
  return end === -1 ? pathname.slice(1) : pathname.slice(1, end);
};

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const cookies = new Cookies();
  const token = cookies.get(`${JWT_COOKIE_KEY}`);

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'x-portal': portalName(),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation }: any) => {
  if (graphQLErrors) {
    const limitError = graphQLErrors.find((e: any) => e.extensions && e.extensions.freePlanLimit);
    if (limitError) {
      window.dispatchEvent(new CustomEvent('free-plan-limit', { detail: { message: limitError.message } }));
    }

    const errorType = 'graphQLError';
    const group = formatMessage(errorType, operation);

    console.groupCollapsed(...group);

    if (process.env.NODE_ENV !== 'production') {
      graphQLErrors.map(({ message, path }: any) => {
        const error = formatError(message, path);
        console.log(...error);
        return { message, path };
      });
    }

    // @ts-ignore
    console.groupEnd(...group);
  }

  if (networkError) {
    const errorType = 'networkError';
    const group = formatMessage(errorType, operation);

    console.groupCollapsed(...group);

    const error = formatError(networkError.message);
    console.log(...error);

    // @ts-ignore
    console.groupEnd(...group);
  }
});

const loggerLink = new ApolloLink((operation, forward): any => {
  const startTime = new Date().getTime();

  return (
    forward &&
    forward(operation).map(result => {
      // @ts-ignore
      const operationType = operation.query.definitions[0].operation;
      const elapsed = new Date().getTime() - startTime;

      const group = formatMessage(operationType, operation, elapsed);
      console.groupCollapsed(...group);

      console.log('INIT', operation);
      console.log('RESULT', result);

      // @ts-ignore
      console.groupEnd(...group);

      return result;
    })
  );
});

const httpLink = new HttpLink({
  uri: `${endpointURL}/graphql`,
});

const wsLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const cookies = new Cookies();
  const token = cookies.get(`${JWT_COOKIE_KEY}`);

  // return the headers to the context so httpLink can read them
  return new WebSocketLink({
    uri: `ws://${endpointURL.host}/subscriptions`,
    options: {
      reconnect: true,
      connectionParams: {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
          'x-portal': portalName(),
        },
      },
    },
  });
});

const splitLink = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

export const client = new ApolloClient({
  link: from([
    authLink,
    loggerLink,
    errorLink,
    splitLink,
    new RetryLink({
      attempts: {
        max: 2,
        // Never auto-retry mutations: a request that times out upstream may still have been
        // executed by the server, so re-sending it duplicates the write (this is how duplicate
        // consultations were created in prod — the retried upsertConsultation inserted a second
        // row). Queries are safe to retry; mutations surface the error to the user instead.
        retryIf: (error, operation) => {
          const definition = getMainDefinition(operation.query);
          const isMutation = definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
          return !!error && !isMutation;
        },
      },
      delay: { initial: 3000 },
    }),
    // @ts-ignore
    uploadLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          filteredInstitutions: {
            // Don't cache separate results based on
            // any of this field's arguments.
            keyArgs: false,
            // Concatenate the incoming list items with
            // the existing list items.
            merge(
              existing = {
                aggregate: { count: 0 },
                pageInfo: {
                  endCursor: '',
                  hasNextPage: false,
                  hasPreviousPage: false,
                  startCursor: '',
                },
                edges: [],
                __typename: '',
              },
              incoming
            ) {
              const newEdges = uniqBy([...existing.edges, ...incoming.edges], 'node.__ref');
              return {
                ...existing,
                pageInfo: incoming.pageInfo,
                edges: newEdges,
              };
            },
          },
        },
      },
    },
  }),
});
